import { useAtom } from 'jotai/react';
import { useEffect } from 'react';
import { useTranslate } from 'react-translate-plus';

import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useToasts } from '../../projects/components/Toast/ToastProvider';
import { useUserProfile } from '../../providers/UserProfileContext/UserProfileContext';
import { getLanguageName } from '../../services/locale';
import { getWindow } from '../../services/windowService';
import { Message } from './Message';
import { userProfileLocaleNeedToShow } from './user-profile-locale-notify-atoms';

export function useUserProfileLocaleToast(): void {
  const t = useTranslate('Notifications');
  const t2 = useTranslate('NavigationBar');

  const [timeToShow, setShown] = useAtom(userProfileLocaleNeedToShow);
  const { addToast } = useToasts();
  const { userData } = useUserProfile();
  const userId = userData?.id;

  const [localStorageLocale] = useLocalStorage<string>('locale');
  const currentLanguage = localStorageLocale?.split('-')[0];
  const profileLanguage = userData?.language?.split('-')[0];

  useEffect(() => {
    const languagesDifferent =
      profileLanguage && currentLanguage && profileLanguage !== currentLanguage;
    if (!userId || !languagesDifferent) {
      return;
    }

    let tId: NodeJS.Timeout = null;
    if (timeToShow) {
      const href = t('USER_PROFILE_URL', {
        origin: t2('TITLE_CONFIGURATOR'),
        originUrl: getWindow().location.href,
      });
      addToast({
        title: t('DIFFERENT_USER_PROFILE_LANGUAGE_TITLE'),
        children: (
          <Message
            linkText={t('DIFFERENT_USER_PROFILE_LANGUAGE_LINK_TEXT')}
            href={href}
            message={t('DIFFERENT_USER_PROFILE_LANGUAGE_MESSAGE', {
              selectedLanguage: getLanguageName(currentLanguage),
              profileLanguage: getLanguageName(profileLanguage),
            })}
            messageEnd={t('DIFFERENT_USER_PROFILE_LANGUAGE_MESSAGE_END')}
          />
        ),
        type: 'default', // 'default' | 'positive' | 'negative' | 'warning'
        timeout: 0,
        onClose: () => {},
      });
      tId = setTimeout(() => {
        setShown();
      }, 3000);
    }
    return () => {
      if (tId !== null) {
        clearTimeout(tId);
      }
    };
  }, [userId, timeToShow, addToast, setShown, profileLanguage, currentLanguage, t, t2]);
}
